'use client';

import { useState } from 'react';

import { Slot } from '@radix-ui/react-slot';
import cx from 'classnames';

import { type Masked, unmask } from '~/contentful/graphql';
import { type Header_AssetFragmentFragment } from '~/contentful/graphql/_generated/graphql';
import { Column, Grid } from '~/ui/components/grid';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Preheader } from '~/ui/modules/header/preheader';
import {
  type Header_AssetFragment,
  type Header_HeroAssetFragment,
} from '~/ui/modules/header/query';
import { HeroAsset, type HeroAssetType } from '~/ui/modules/heroAsset';
import { getImageAspectRatio } from '~/ui/modules/image/getImageAspectRatio';
import { Breakpoint } from '~/ui/styles/grid';
import gridStyles from '~/ui/styles/grid.module.scss';
import typographyStyles from '~/ui/styles/typography.module.scss';
import { getOrientation } from '~/ui/utils/aspectRatio';
import { CreditSection, type Credits } from '~/v1/modules/header/creditSection';
import { Breadcrumb } from '~/v1/system/breadcrumb';

import styles from './header.module.scss';

export interface HeaderModuleProps {
  title: string;
  description?: string;
  eyebrow?: string;
  quickLinks?: React.ReactNode;
  credits?: Credits;
  rawDateCredit?: boolean;
  preheaderAction?: React.ReactNode;
  hero?: Masked<typeof Header_AssetFragment | typeof Header_HeroAssetFragment>;
  /**
   * Determines the amount of spacing after the header
   * @default false
   * @example
   *  /events
   *  /news
   */
  isLandingPage?: boolean;
  /**
   * Determines the size of the title
   * @default false
   * @example
   *  /history
   *  /mission
   */
  isRootPage?: boolean;
}

export function HeaderModule({
  title,
  description,
  eyebrow,
  quickLinks,
  credits,
  rawDateCredit,
  preheaderAction,
  hero: heroData,
  isLandingPage = false,
  isRootPage = false,
}: HeaderModuleProps) {
  let image: Header_AssetFragmentFragment | undefined;
  let videoSrc: string | undefined | null;

  const hero = unmask<HeroAssetType>(heroData);
  if (hero) {
    image = unmask<typeof Header_AssetFragment>(
      hero
        ? 'image' in hero
          ? (hero.image as Masked<typeof Header_AssetFragment>)
          : (hero as Masked<typeof Header_AssetFragment>)
        : undefined,
    );
    videoSrc = 'video' in hero ? hero.video?.url : undefined;
  }

  const isSmallBp = useBreakpoint(Breakpoint.MOBILE);
  const [aspectRatio, setAspectRatio] = useState(() =>
    image ? getImageAspectRatio(image) : undefined,
  );

  const hasPortraitImage = videoSrc
    ? false
    : aspectRatio
      ? getOrientation(aspectRatio) === 'portrait'
      : undefined;

  const titleClassName = isRootPage
    ? typographyStyles.pageTitleLarge
    : typographyStyles.pageTitleSmall;
  const topTextClass = eyebrow ? typographyStyles.bodyLarge : titleClassName;

  const imageProps = hasPortraitImage
    ? {
        colSpan: {
          [Breakpoint.MOBILE]: 5,
          [Breakpoint.TABLET]: 4,
          [Breakpoint.DESKTOP]: 5,
        },
        aspectRatio: '4/5',
      }
    : {
        colSpan: {
          [Breakpoint.MOBILE]: 5,
          [Breakpoint.TABLET]: 7,
          [Breakpoint.DESKTOP]: 9,
        },
        aspectRatio: '3/2',
      };

  return (
    <Grid>
      <Column span={{ [Breakpoint.MOBILE]: 5, [Breakpoint.TABLET]: 7, [Breakpoint.DESKTOP]: 9 }}>
        <header
          className={cx(styles.root, topTextClass, {
            [styles.isLandingPage]: isLandingPage,
            [styles.portraitImage]: hasPortraitImage,
          })}
        >
          <Preheader left={<Breadcrumb />} right={preheaderAction} className={styles.preheader} />
          <div className={styles.spacer} />
          {eyebrow && (
            <div className={cx(styles.eyebrow, typographyStyles.bodyLarge)}>{eyebrow}</div>
          )}
          <div className={styles.titleAndDescription}>
            <h1 className={titleClassName}>{title}</h1>
            {description && (
              <p className={cx(styles.description, typographyStyles.modulesTitle)}>{description}</p>
            )}
          </div>
          {quickLinks && (
            <Slot className={cx(styles.quickLinks, gridStyles.overflowRight)}>{quickLinks}</Slot>
          )}
          {credits && (
            <CreditSection
              credits={credits}
              rawDate={rawDateCredit}
              className={styles.creditSection}
            />
          )}
          {image || heroData ? (
            <div
              className={cx(styles.hero, gridStyles.overflowRight, {
                [gridStyles.overflowLeft]: !hasPortraitImage || isSmallBp,
              })}
            >
              {heroData && (
                <HeroAsset
                  data={heroData}
                  imageProps={{
                    priority: true,
                    onCalculateAspectRatio: setAspectRatio,
                    ...imageProps,
                  }}
                />
              )}

              {image?.description ? (
                <Column
                  span={{
                    [Breakpoint.TABLET]: hasPortraitImage ? undefined : 5,
                    [Breakpoint.DESKTOP]: hasPortraitImage ? undefined : 5,
                  }}
                  offsetLeft={{
                    [Breakpoint.DESKTOP]: hasPortraitImage ? 0 : 1,
                  }}
                  className={styles.caption}
                >
                  <span className={typographyStyles.caption}>{image.description}</span>
                </Column>
              ) : null}
            </div>
          ) : null}
        </header>
      </Column>
    </Grid>
  );
}
